import { useIssuesStore } from "@/store/zustand/issues-store";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { useCanvasId } from "@/utils/utils";
import { hashStringToHex } from "@/utils/hashUtils";
import type { ReactFramework } from "@/utils/templateProjects";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { getStoriesForComponent } from "@/utils/jsx/extractElementsUtils";
import { STORYBOARD_TYPES } from "@/utils/constants";

/**
 * Uploads an instance screenshot to GCS and returns the public URL.
 *
 * Path structure: instance-screenshots/{canvasId}/{storyboardId}/{componentHash}/{instanceCodebaseId}.png
 *
 * Where componentHash is a hash of: canvas-root-relative pathname + export name
 * This ensures all instances of the same component in the same canvas share the same folder.
 *
 * NOTE: GCS bucket should have lifecycle rules configured for 30-day auto-deletion.
 * This needs to be set up on the bucket itself (tempo-image-previews) with a rule like:
 * - Condition: age > 30 days, prefix: instance-screenshots/
 * - Action: Delete
 * @returns Promise<string | null> - The public URL or null on failure
 */
export async function uploadInstanceScreenshot({
  blob,
  canvasId,
  storyboardId,
  componentFilename,
  componentName,
  instanceCodebaseId,
  supabaseJWT,
}: {
  blob: Blob;
  canvasId: string;
  storyboardId: string;
  componentFilename: string;
  componentName: string;
  instanceCodebaseId: string;
  supabaseJWT: string;
}): Promise<string | null> {
  try {
    // Create a stable hash from component path + name
    const componentHash = hashStringToHex(
      `${componentFilename}:${componentName}`,
    );

    // Build the folder path
    const folderPath = `instance-screenshots/${canvasId}/${storyboardId}/${componentHash}`;
    const fileName = `${instanceCodebaseId}.png`;

    // Convert blob to base64
    const arrayBuffer = await blob.arrayBuffer();
    const base64Data = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        "",
      ),
    );

    // Upload to GCS via the imageUpload endpoint
    const uploadResponse = await fetch(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/imageUpload`,
      {
        method: "POST",
        body: JSON.stringify({
          file: base64Data,
          fileName,
          folderPath,
          mimeType: "image/png",
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseJWT}`,
        },
      },
    );

    if (!uploadResponse.ok) {
      console.error(
        "[Instance Screenshot] Upload failed:",
        uploadResponse.statusText,
      );
      return null;
    }

    const uploadData = await uploadResponse.json();

    if (uploadData.error) {
      console.error("[Instance Screenshot] Upload error:", uploadData.error);
      return null;
    }

    console.log("[Instance Screenshot] Uploaded:", uploadData.url);
    return uploadData.url;
  } catch (error) {
    console.error("[Instance Screenshot] Upload error:", error);
    return null;
  }
}

export function useStoryboardName(storyboardId: string): string {
  const canvasId = useCanvasId();

  // Check pagesStore first for storyboard info (has route data for application storyboards)
  const storyboardFromPages = usePagesStore((state) => {
    const pages = state.pages[canvasId || ""];
    if (!pages) return null;

    for (const page of pages) {
      const storyboard = page.storyboards.find((sb) => sb.id === storyboardId);
      if (storyboard) {
        // For application storyboards, show the route
        if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
          return storyboard.route || "/";
        }
        // For other storyboards, use name or storyComponentName
        return storyboard.name || storyboard.storyComponentName || null;
      }
    }
    return null;
  });

  // Fall back to issuesStore if not found in pagesStore
  const storyboardFromIssues = useIssuesStore((state) => {
    if (storyboardFromPages) return null; // Skip if already found

    const canvas = state.currentCanvas[canvasId || ""];
    if (!canvas?.storyboards) return null;

    const storyboard = canvas.storyboards.find((sb) => sb.id === storyboardId);
    if (storyboard) {
      return (
        storyboard.name || storyboard.storyComponentName || storyboard.route
      );
    }
    return null;
  });

  const storyboardName = storyboardFromPages || storyboardFromIssues;

  // Fallback to truncated ID if not found
  return storyboardName || `Storyboard ${storyboardId.slice(0, 4)}`;
}

/**
 * Finds an existing story file for a component by checking the treeElementLookup
 * for StorybookComponent elements that match the component.
 */
export function findExistingStoryFile(
  project: { framework?: ReactFramework },
  treeElementLookup: Record<string, JsxElement>,
  component: JsxElement,
): string | null {
  const stories = getStoriesForComponent(project, treeElementLookup, component);

  if (stories.length === 0) {
    return null;
  }

  // Return the filename of the first story (they should all be in the same file)
  return stories[0].filename;
}
